import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icons,
  iconSize,
  Link,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import type { DocumentSignoffView, FormSummary } from '@munifocus/contracts-gov';
import { useFileSignoffs, useSubmitSignoff, useWithdrawSignoff } from '../../lib/signoff-api';
import { useMutationToast } from '../../lib/mutation-toast';
import { formatDateTime } from '../../lib/relative-time';
import { DecisionDialog } from './DecisionDialog';

/**
 * Sign-off for the published version of one document: submit it for review,
 * see where it stands, and — for a reviewer who may sign it — decide.
 *
 * Renders nothing while the municipality has sign-off off, or for a document
 * that was not issued as a PDF (only a PDF can carry a stamped signature).
 */
export function SignoffPanel({ fileId, published }: { fileId: string; published: FormSummary }) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const signoffs = useFileSignoffs(fileId);
  const submit = useSubmitSignoff(fileId);
  const withdraw = useWithdrawSignoff(fileId);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [reviewing, setReviewing] = useState<DocumentSignoffView | null>(null);

  if (!signoffs.data?.enabled || published.format !== 'pdf') return null;

  const items = signoffs.data.items;
  const signed = items.find((s) => s.signedFormId === published.id && s.status === 'approved');
  const latest = items.find((s) => s.formId === published.id);

  if (signed) {
    return (
      <Stack spacing={0.5}>
        <Stack
          useFlexGap
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <StatusChip status="approved" label={t('gov.signoff.status.signed')} />
          <Typography variant="caption" color="text.secondary">
            {t('gov.signoff.signedBy', {
              name: signed.reviewerName ?? '',
              at: formatDateTime(signed.signedAt ?? '', i18n.language),
            })}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('gov.signoff.reference', { reference: signed.verificationRef ?? '' })}{' '}
          <Link
            component={RouterLink}
            to={`/verify-document?ref=${signed.verificationRef ?? ''}`}
            underline="hover"
          >
            {t('gov.signoff.verifyLink')}
          </Link>
        </Typography>
      </Stack>
    );
  }

  const mine = Boolean(latest?.isMine);

  return (
    <Stack spacing={0.75}>
      {latest?.status === 'pending' && (
        <Stack
          useFlexGap
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <StatusChip status="pending" label={t('gov.signoff.status.pending')} />
          <Typography variant="caption" color="text.secondary">
            {t('gov.signoff.submittedBy', {
              name: latest.submittedByName,
              at: formatDateTime(latest.submittedAt, i18n.language),
            })}
          </Typography>
          {latest.canDecide && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Icons.FileSignature size={iconSize.md} />}
              onClick={() => setReviewing(latest)}
            >
              {t('gov.signoff.review')}
            </Button>
          )}
          {mine && (
            <Button
              size="small"
              variant="text"
              disabled={withdraw.isPending}
              onClick={() =>
                withdraw.mutate(latest.id, {
                  onSuccess: () => toast.success('gov.signoff.withdrawn'),
                  onError: (err) => toast.error(err, 'gov.signoff.withdrawFailed'),
                })
              }
            >
              {t('gov.signoff.withdraw')}
            </Button>
          )}
        </Stack>
      )}

      {(latest?.status === 'changes_requested' || latest?.status === 'rejected') && (
        <Alert severity={latest.status === 'rejected' ? 'error' : 'warning'}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t(`gov.signoff.status.${latest.status}`)} — {latest.reviewerName}
          </Typography>
          {latest.decisionComment && (
            <Typography variant="body2">{latest.decisionComment}</Typography>
          )}
        </Alert>
      )}

      {latest?.status !== 'pending' && (
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icons.FileSignature size={iconSize.md} />}
            disabled={submit.isPending}
            onClick={() => setAsking(true)}
          >
            {t('gov.signoff.submit')}
          </Button>
        </Box>
      )}

      <Dialog open={asking} onClose={() => setAsking(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.signoff.submitTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.signoff.submitHint')}
            </Typography>
            <TextField
              label={t('gov.signoff.noteLabel')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsking(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={submit.isPending}
            onClick={() =>
              submit.mutate(
                { formId: published.id, note: note.trim() || undefined },
                {
                  onSuccess: () => {
                    toast.success('gov.signoff.submitted');
                    setAsking(false);
                    setNote('');
                  },
                  onError: (err) => toast.error(err, 'gov.signoff.submitFailed'),
                },
              )
            }
          >
            {t('gov.signoff.submitConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {reviewing && <DecisionDialog signoff={reviewing} open onClose={() => setReviewing(null)} />}
    </Stack>
  );
}
